import { useState } from "react";
import { AiOutlineLoading } from "react-icons/ai";
import { Button } from "flowbite-react";
import { useTranslation } from "react-i18next";
import { HiExclamation } from "react-icons/hi";

import { StatusDot } from "../../../../components";
import { useHubspot } from "../../../../contexts";
import { getTimeDifferenceApproximation } from "../../../../utils";
import { DisconnectConfirmation } from "../DisconnectConfirmation";

// The backend's `disconnected` bucket, for an API that predates `connection_health`.
// Without this, a legacy INACTIVE row (INACTIVE is the model's DEFAULT) rendered green with
// "You are connected to HubSpot" — the incident's symptom set, in the one arm that had no
// fallback while its siblings did.
const LEGACY_DISCONNECTED_STATUSES = ["INACTIVE", "DELETED", "DISCONNECTED"];

// The single status surface for the integration: connection dot, sync status
// text, and the connect / disconnect / reconnect / resync actions.
export const HsStatusBar = () => {
  const {
    loading,
    connectLoading,
    disconnectLoading,
    reconnectLoading,
    syncLoading,
    hsIntegration,
    hasIntegration,
    connectionHealth,
    isHubspotFieldsSynced,
    needsReconnection,
    lastSyncInfo,
    error,
    handleHubSpotConnect,
    handleHubSpotDisconnect,
    handleHubSpotReconnect,
    syncHubspotFields,
  } = useHubspot();
  const { t } = useTranslation();

  const [isResyncing, setIsResyncing] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const isBusy =
    connectLoading ||
    disconnectLoading ||
    reconnectLoading ||
    loading ||
    syncLoading ||
    isResyncing;

  const handleResync = async () => {
    setIsResyncing(true);
    try {
      await syncHubspotFields();
    } catch (err) {
      // Error already surfaced as a toast in syncHubspotFields.
    } finally {
      setIsResyncing(false);
    }
  };

  // A connected-looking record is not a working connection (PAN-258). Green requires
  // the backend to say `healthy`; an unfinished setup is amber, never green.
  //
  // All three derivations below carry a fallback for an API that predates
  // `connection_health` — one per bucket, with disjoint status sets — because
  // a frontend-first deploy is a real ordering. Without the PENDING_SETUP arm below, the
  // 2026-07-31 row rendered green + Disconnect + Resync + no banner in that window — the
  // incident reproduced verbatim by the code written to delete it. `status` has been in this
  // payload all along; the legacy source existed and simply was not used.
  const isSetupIncomplete = connectionHealth
    ? connectionHealth === "setup_incomplete"
    : hsIntegration?.status === "PENDING_SETUP";
  const isDisconnected = connectionHealth
    ? connectionHealth === "disconnected"
    : LEGACY_DISCONNECTED_STATUSES.includes(hsIntegration?.status);
  const isHealthy = connectionHealth
    ? connectionHealth === "healthy"
    : hasIntegration &&
      !needsReconnection &&
      !isSetupIncomplete &&
      !isDisconnected;
  // Offer Disconnect whenever there is a credential left to revoke — NOT merely when the
  // integration looks healthy. `destroy` is the only way a customer can revoke our access
  // to their CRM, and INACTIVE (the model's default) maps to the `disconnected` bucket
  // while often still holding a live Nango connection. Gating this on health alone made
  // that endpoint unreachable from the UI for exactly those rows.
  const offersDisconnect =
    hasIntegration && Boolean(hsIntegration?.connection_id);
  // Resync stays available during setup_incomplete — it is how the wizard pulls
  // pipelines in the first place, and the status text tells the user to click it.
  // `&& !needsReconnection` would be dead here: a broken connection makes both isHealthy and
  // isSetupIncomplete false in either mode, so the two terms already exclude it.
  const offersResync = isHealthy || isSetupIncomplete;

  const getStatusColor = () => {
    if (!hasIntegration) return undefined;
    if (needsReconnection) return "red";
    if (isSetupIncomplete) return "yellow";
    if (!isHealthy) return undefined;
    return "green";
  };

  const getStatusText = () => {
    if (!hasIntegration) return t("pages.settings.hubspot.description");
    if (syncLoading || isResyncing)
      return t("pages.settings.hubspot.resyncing");
    if (needsReconnection)
      return "Connection expired — data sync has stopped. Reconnect to resume.";
    // Must precede every "connected" branch below. Without it a disconnected record fell
    // through to "You are connected to HubSpot. Successfully synced 5 minutes ago." — the
    // exact class of lie this whole change exists to delete, one bucket over.
    // Name only actions that are actually on screen. Reconnect is gated on
    // `needsReconnection`, which is false for this bucket, so telling the customer to
    // "reconnect to resume" pointed at a button that is not rendered.
    if (isDisconnected)
      return offersDisconnect
        ? "Not syncing. Disconnect to revoke access."
        : "Not connected — no data is syncing.";
    // "No pipelines yet" is the more actionable message and is itself a setup-incomplete
    // state, so it wins; the generic one covers a setup abandoned *after* the field sync.
    if (!isHubspotFieldsSynced)
      return t("pages.settings.hubspot.fieldsSyncStatus.noPipelines");
    if (isSetupIncomplete)
      return "Setup not finished — no data is syncing. Finish the steps below to start.";
    if (["SYNC_ERROR", "FAILED"].includes(lastSyncInfo.status)) {
      return (
        <span className="flex flex-row items-center gap-2">
          <HiExclamation size={20} className="shrink-0 text-red-700" />
          {t("pages.settings.hubspot.lastFailedSync", {
            timestamp: lastSyncInfo.at,
          })}
        </span>
      );
    }
    if (lastSyncInfo.status === "SUCCESS" && lastSyncInfo.at) {
      const timeApprox = getTimeDifferenceApproximation(lastSyncInfo.at);
      return (
        t("pages.settings.hubspot.connected") +
        " " +
        t(
          `pages.settings.hubspot.lastSuccessSync.${Object.keys(timeApprox)[0]}`,
          timeApprox,
        )
      );
    }
    return t("pages.settings.hubspot.fieldsSyncStatus.completed");
  };

  return (
    <>
      {error && (
        <div className="px-6">
          <div className="rounded-md bg-red-50 p-3">
            <div className="flex">
              <HiExclamation className="h-5 w-5 shrink-0 text-red-400" />
              <p className="ml-3 text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-row flex-wrap items-center justify-between gap-4 px-6">
        <div className="flex min-w-0 flex-row items-center gap-2.5">
          <img
            className="mr-2 h-12 w-36 shrink-0"
            src="/static/svg/hubspot-text.svg"
            alt="hubspot"
          />
          {loading || syncLoading || isResyncing ? (
            <AiOutlineLoading className="h-5 w-5 shrink-0 animate-spin text-blue-500" />
          ) : (
            <StatusDot status={getStatusColor()} />
          )}
          <div className="text-sm leading-6 text-gray-600">
            {getStatusText()}
          </div>
        </div>

        <div className="flex flex-row items-center gap-2">
          {offersResync && (
            <Button
              color="gray"
              className="w-36"
              disabled={isBusy}
              isProcessing={isResyncing}
              onClick={handleResync}
            >
              {t("pages.settings.resync")}
            </Button>
          )}
          {needsReconnection && (
            <Button
              color="warning"
              className="w-36"
              disabled={isBusy}
              isProcessing={reconnectLoading}
              onClick={handleHubSpotReconnect}
            >
              {t("common.reconnect")}
            </Button>
          )}
          <Button
            color="blue"
            className="w-36"
            disabled={isBusy}
            isProcessing={connectLoading || disconnectLoading}
            onClick={() =>
              offersDisconnect
                ? setShowDisconnectConfirm(true)
                : handleHubSpotConnect()
            }
          >
            {offersDisconnect ? t("common.disconnect") : t("common.connect")}
          </Button>
        </div>
      </div>

      <DisconnectConfirmation
        show={showDisconnectConfirm}
        integrationName="HubSpot"
        onCancel={() => setShowDisconnectConfirm(false)}
        onConfirm={() => {
          setShowDisconnectConfirm(false);
          handleHubSpotDisconnect(hsIntegration);
        }}
      />
    </>
  );
};
